export type EmailDomainProblem =
  | { kind: "typo"; suggestion: string }
  | { kind: "reserved" };

interface ProviderDomain {
  host: string;
  tld: string;
  fuzzy: boolean;
}

function providers(domains: ReadonlyArray<string>, fuzzy: boolean): Array<ProviderDomain> {
  return domains.map((domain) => {
    const separator = domain.lastIndexOf(".");
    return { host: domain.slice(0, separator), tld: domain.slice(separator + 1), fuzzy };
  });
}

const PROVIDER_DOMAINS: ReadonlyArray<ProviderDomain> = [
  ...providers(
    [
      "gmail.com",
      "googlemail.com",
      "yahoo.com",
      "hotmail.com",
      "outlook.com",
      "icloud.com",
      "comcast.net",
      "sbcglobal.net",
      "verizon.net",
      "bellsouth.net",
      "earthlink.net",
      "optonline.net",
      "protonmail.com",
      "repackify.com",
    ],
    true,
  ),
  ...providers(
    ["ymail.com", "live.com", "msn.com", "aol.com", "me.com", "mac.com", "att.net", "charter.net", "cox.net"],
    false,
  ),
];

const KNOWN_REAL_DOMAINS: ReadonlySet<string> = new Set([
  "mail.com",
  "email.com",
  "gmx.com",
  "gmx.net",
  "cloud.com",
  "aim.com",
  "aon.com",
  "wahoo.com",
  "yahoo.co.uk",
  "hotmail.co.uk",
  "live.ca",
  "comcast.com",
  "verizon.com",
  "att.com",
  "proton.me",
  "pm.me",
  "resend.dev",
]);

const MISTYPED_TLDS: Readonly<Record<string, string>> = {
  con: "com",
  cmo: "com",
  comc: "com",
  cim: "com",
  ocm: "com",
  cpm: "com",
  vom: "com",
  xom: "com",
  coom: "com",
  comm: "com",
  comf: "com",
  comd: "com",
  comv: "com",
  cpom: "com",
  ccom: "com",
  comn: "com",
  conm: "com",
  ocom: "com",
  nte: "net",
  ner: "net",
  ogr: "org",
};

const RESERVED_DOMAINS: ReadonlyArray<string> = [
  "example.com",
  "example.org",
  "example.net",
  "localhost",
  "test.repackify.com",
];

const RESERVED_TLDS: ReadonlySet<string> = new Set([
  "test",
  "invalid",
  "example",
  "localhost",
]);

const LONG_HOST_LENGTH = 9;

function cell(table: ReadonlyArray<ReadonlyArray<number>>, row: number, column: number): number {
  return table[row]?.[column] ?? Number.POSITIVE_INFINITY;
}

function editDistance(left: string, right: string): number {
  const table: Array<Array<number>> = [Array.from({ length: right.length + 1 }, (_, column) => column)];
  for (let row = 1; row <= left.length; row += 1) {
    const current = [row];
    table.push(current);
    for (let column = 1; column <= right.length; column += 1) {
      const cost = left[row - 1] === right[column - 1] ? 0 : 1;
      let best = Math.min(
        cell(table, row - 1, column) + 1,
        cell(table, row, column - 1) + 1,
        cell(table, row - 1, column - 1) + cost,
      );
      if (
        row > 1 &&
        column > 1 &&
        left[row - 1] === right[column - 2] &&
        left[row - 2] === right[column - 1]
      ) {
        best = Math.min(best, cell(table, row - 2, column - 2) + 1);
      }
      current.push(best);
    }
  }
  return cell(table, left.length, right.length);
}

function allowedDistance(provider: ProviderDomain): number {
  if (!provider.fuzzy) return 0;
  return provider.host.length >= LONG_HOST_LENGTH ? 2 : 1;
}

function tldMatchesProvider(tld: string, provider: ProviderDomain): boolean {
  if (tld === provider.tld) return true;
  return provider.fuzzy && tld === "co" && provider.tld === "com";
}

function closestProviderDomain(host: string, tld: string): string | undefined {
  let best: { domain: string; distance: number } | undefined;
  for (const provider of PROVIDER_DOMAINS) {
    if (!tldMatchesProvider(tld, provider)) continue;
    const distance = editDistance(host, provider.host);
    if (distance > allowedDistance(provider)) continue;
    if (!best || distance < best.distance) {
      best = { domain: `${provider.host}.${provider.tld}`, distance };
    }
  }
  return best?.domain;
}

export function isReservedEmailDomain(domain: string): boolean {
  const normalized = domain.trim().toLowerCase();
  if (RESERVED_TLDS.has(normalized.slice(normalized.lastIndexOf(".") + 1))) return true;
  return RESERVED_DOMAINS.some(
    (reserved) => normalized === reserved || normalized.endsWith(`.${reserved}`),
  );
}

export function suggestEmailDomain(domain: string): string | undefined {
  const normalized = domain.trim().toLowerCase();
  if (KNOWN_REAL_DOMAINS.has(normalized)) return undefined;
  const separator = normalized.lastIndexOf(".");
  if (separator <= 0) return undefined;
  const host = normalized.slice(0, separator);
  const typedTld = normalized.slice(separator + 1);
  const tld = MISTYPED_TLDS[typedTld] ?? typedTld;
  const provider = closestProviderDomain(host, tld);
  const corrected = provider ?? (tld === typedTld ? undefined : `${host}.${tld}`);
  return corrected === normalized ? undefined : corrected;
}

const SEND_REFUSED_PROVIDER_DOMAINS: ReadonlySet<string> = new Set([
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "icloud.com",
]);

function hostOf(domain: string): string {
  return domain.slice(0, domain.lastIndexOf("."));
}

function tldOf(domain: string): string {
  return domain.slice(domain.lastIndexOf(".") + 1);
}

export function isUnsendableTypoDomain(domain: string): boolean {
  const normalized = domain.trim().toLowerCase();
  const suggestion = suggestEmailDomain(normalized);
  if (suggestion === undefined) return false;
  if (hostOf(suggestion) === hostOf(normalized)) return true;
  return SEND_REFUSED_PROVIDER_DOMAINS.has(suggestion) && tldOf(suggestion) === tldOf(normalized);
}

export function diagnoseEmailDomain(
  email: string,
  options: { rejectReserved: boolean },
): EmailDomainProblem | undefined {
  const trimmed = email.trim();
  const separator = trimmed.lastIndexOf("@");
  if (separator <= 0) return undefined;
  const local = trimmed.slice(0, separator);
  const domain = trimmed.slice(separator + 1).toLowerCase();
  if (isReservedEmailDomain(domain)) {
    return options.rejectReserved ? { kind: "reserved" } : undefined;
  }
  const suggestedDomain = suggestEmailDomain(domain);
  return suggestedDomain ? { kind: "typo", suggestion: `${local}@${suggestedDomain}` } : undefined;
}
